/* cspell:ignore noninteractive */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions --
   The <code> is focusable and handles arrows only while it overflows, for keyboard arrow-scroll
   (axe rule: scrollable-region-focusable). */
import cx from "classnames";
import { KeyboardEvent, useCallback, useLayoutEffect, useRef, useState } from "react";
import { useResizeObserver } from "@react-aria/utils";

import { Copy } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import useCopyToClipboard from "hooks/useCopyToClipboard";

import styles from "./styles.module.css";

type CodeSnippetInlineProps = {
  children: string;
  allowCopy?: boolean;
  copyTitle?: string;
  onCopy?: () => void;
  className?: string;
};

const CodeSnippetInline = ({
  children,
  allowCopy = false,
  copyTitle = "Copy",
  onCopy,
  className,
}: CodeSnippetInlineProps) => {
  const handleCopy = useCopyToClipboard(children, onCopy);
  const codeRef = useRef<HTMLElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateIsScrollable = useCallback(() => {
    const code = codeRef.current;

    if (code) {
      setIsScrollable(code.scrollWidth > code.clientWidth);
    }
  }, []);

  useResizeObserver({ ref: codeRef, onResize: updateIsScrollable });
  useLayoutEffect(updateIsScrollable, [updateIsScrollable, children]);

  // Keep arrows for native scrolling until the edge, then let them through to a parent (e.g. table navigation)
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
      return;
    }

    const code = e.currentTarget;
    const maxScrollLeft = code.scrollWidth - code.clientWidth;
    const canScroll =
      e.key === "ArrowLeft" ? code.scrollLeft > 0 : code.scrollLeft < maxScrollLeft - 1;

    if (canScroll) {
      e.stopPropagation();
    }
  };

  return (
    <span className={cx(styles.snippet, className)}>
      <code
        ref={codeRef}
        className={styles.code}
        tabIndex={isScrollable ? 0 : undefined}
        onKeyDown={isScrollable ? handleKeyDown : undefined}
      >
        {children}
      </code>
      {allowCopy && (
        <ButtonIcon
          className={styles.copy}
          size="small"
          variant="ghost"
          icon={Copy}
          onPress={handleCopy}
        >
          {copyTitle}
        </ButtonIcon>
      )}
    </span>
  );
};

CodeSnippetInline.displayName = "DS.CodeSnippetInline";

export default CodeSnippetInline;
